import { Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useContext, useMemo } from 'react';
import { TimelineContext } from '@/contexts/timelineContexts';
import { PatientProfileEdit, savePatientProfileEdit } from '@/services/patientProfile';
import { canUpdateResourceType } from '@/services/permissions';

/**
 * The save handler for the card's profile rows, or undefined when they must stay
 * read-only: a saved note (a record of the call, not an editor), no patient
 * context to write through, or a role whose policy cannot update a Patient.
 *
 * `useContext`, not `useTimelineContext`: that one throws without a provider,
 * and an editable row must not decide whether the card renders.
 */
export function usePatientProfileEditor(
  patient: Patient | undefined,
  readOnly: boolean
): ((edit: PatientProfileEdit) => Promise<void>) | undefined {
  const medplum = useMedplum();
  const applyPatientEdit = useContext(TimelineContext)?.patientContext?.applyPatientEdit;

  return useMemo(() => {
    if (readOnly || !patient || !applyPatientEdit || !canUpdateResourceType(medplum, 'Patient')) {
      return undefined;
    }
    return (edit: PatientProfileEdit) => savePatientProfileEdit(medplum, { patient, applyPatientEdit, edit });
  }, [medplum, patient, readOnly, applyPatientEdit]);
}
